export interface ProcessedIndicator {
  id: number;
  indicador: string;
  meta: number;
  fecha_inicio: Date;
  fecha_fin: Date;
  planificado: number;
  ejecutado: number;
  porcentaje: number;
  estado: "sobrecumplido" | "completado" | "en-progreso" | "en-riesgo" | "atrasado";
}

export interface ProcessedTask {
  id: number;
  codigo: string;
  descripcion: string;
  peso: number;
  proyecto: string;
  proyectoCodigo: string;
  fase: string;
  faseSigla: string;
  actividad: string;
  codigoActividad: string; // 🆕 nuevo campo
  indicadores: ProcessedIndicator[];
  porcentajePromedio: number;
  estado: "sobrecumplido" | "completado" | "en-progreso" | "en-riesgo" | "atrasado";
}

export interface ProcessedPhase {
  sigla: string;
  nombre: string;
  tareas: ProcessedTask[];
  porcentajePromedio: number;
}

export interface ProcessedProject {
  oid: number;
  codigo: string;
  nombre: string;
  fases: ProcessedPhase[];
  totalTareas: number;
  tareasCompletadas: number;
  porcentajePromedio: number;
}

export interface ProcessedData {
  proyectos: ProcessedProject[];
  resumen: {
    totalProyectos: number;
    totalTareas: number;
    tareasCompletadas: number;
    tareasPendientes: number;
    porcentajeGlobal: number;
  };
  todasLasTareas: ProcessedTask[];
}

export interface ActivityProgress {
  id: number;
  codigo: string;
  descripcion: string;
  fase: string;
  porcentajePonderado: number;
  tareas: ProcessedTask[];
}

export interface ProjectProgress {
  oid: number;
  codigo: string;
  nombre: string;
  porcentajePonderado: number;
  actividades: ActivityProgress[];
}

//
// 🚦 Semáforo actualizado
//
function determineStatus(
  porcentaje: number
): "sobrecumplido" | "completado" | "en-progreso" | "en-riesgo" | "atrasado" {
  if (porcentaje > 100) return "sobrecumplido";
  if (porcentaje === 100) return "completado";
  if (porcentaje >= 75) return "en-progreso";
  if (porcentaje >= 25) return "en-riesgo";
  return "atrasado";
}

//
// 🔹 Procesamiento de datos
//
export function processProjectData(rawData: any): ProcessedData {
  const proyectos: ProcessedProject[] = [];
  const todasLasTareas: ProcessedTask[] = [];
  let totalTareas = 0;
  let tareasCompletadas = 0;
  let sumaPorcentajes = 0;

  for (const proyecto of rawData.data) {
    const fasesSet = new Set<string>();
    const actividadesProcesadas: any[] = [];

    for (const actividad of proyecto.actividades) {
      fasesSet.add(actividad.fase.sigla);
      const tareasProcesadas: ProcessedTask[] = [];

      for (const tarea of actividad.tareas) {
        const indicadoresProcesados: ProcessedIndicator[] = tarea.indicadores.map((ind: any) => {
          const planificado = Number.parseFloat(ind.cronograma.P);
          const ejecutado = Number.parseFloat(ind.cronograma.E);
          const porcentaje = ind.cronograma.porcentaje;

          return {
            id: ind.id,
            indicador: ind.indicador,
            meta: Number.parseFloat(ind.meta),
            fecha_inicio: new Date(ind.fecha_inicio),
            fecha_fin: new Date(ind.fecha_fin),
            planificado,
            ejecutado,
            porcentaje,
            estado: determineStatus(porcentaje),
          };
        });

        const porcentajePromedio =
          indicadoresProcesados.length > 0
            ? indicadoresProcesados.reduce((sum, ind) => sum + ind.porcentaje, 0) /
              indicadoresProcesados.length
            : 0;

        const tareaProcesada: ProcessedTask = {
          id: tarea.id,
          codigo: tarea.codigo_tarea,
          descripcion: tarea.descripcion,
          peso: tarea.pesoPorcentual,
          proyecto: proyecto.proyecto,
          proyectoCodigo: proyecto.codigo,
          fase: actividad.fase.nombre_corto,
          faseSigla: actividad.fase.sigla,
          actividad: actividad.descripcion,
          codigoActividad: actividad.codigo_actividad, // 🆕 agregado aquí
          indicadores: indicadoresProcesados,
          porcentajePromedio,
          estado: determineStatus(porcentajePromedio),
        };

        tareasProcesadas.push(tareaProcesada);
        todasLasTareas.push(tareaProcesada);
        totalTareas++;
        sumaPorcentajes += porcentajePromedio;
        if (porcentajePromedio >= 100) tareasCompletadas++;
      }

      const porcentajeActividad =
        tareasProcesadas.length > 0
          ? tareasProcesadas.reduce((sum, t) => sum + t.porcentajePromedio, 0) /
            tareasProcesadas.length
          : 0;

      actividadesProcesadas.push({
        id: actividad.id,
        codigo: actividad.codigo_actividad,
        descripcion: actividad.descripcion,
        fase: actividad.fase.nombre_corto,
        siglaFase: actividad.fase.sigla,
        tareas: tareasProcesadas,
        porcentajePromedio: porcentajeActividad,
      });
    }

    const fases: ProcessedPhase[] = Array.from(fasesSet).map((sigla) => {
      const actividadesDeFase = actividadesProcesadas.filter((a) => a.siglaFase === sigla);
      const tareasDeFase = ([] as ProcessedTask[]).concat(
        ...actividadesDeFase.map((a) => a.tareas)
      );

      const porcentajePromedio: number =
        tareasDeFase.length > 0
          ? tareasDeFase.reduce((sum, t) => sum + t.porcentajePromedio, 0) / tareasDeFase.length
          : 0;

      return {
        sigla,
        nombre: actividadesDeFase[0]?.fase || "",
        tareas: tareasDeFase,
        porcentajePromedio,
      };
    });

    const porcentajeProyecto =
      fases.length > 0
        ? fases.reduce((sum, f) => sum + f.porcentajePromedio, 0) / fases.length
        : 0;

    proyectos.push({
      oid: proyecto.oid,
      codigo: proyecto.codigo,
      nombre: proyecto.proyecto,
      fases,
      totalTareas: todasLasTareas.filter((t) => t.proyectoCodigo === proyecto.codigo).length,
      tareasCompletadas: todasLasTareas.filter(
        (t) => t.proyectoCodigo === proyecto.codigo && t.porcentajePromedio >= 100
      ).length,
      porcentajePromedio: porcentajeProyecto,
    });
  }

  return {
    proyectos,
    resumen: {
      totalProyectos: proyectos.length,
      totalTareas,
      tareasCompletadas,
      tareasPendientes: totalTareas - tareasCompletadas,
      porcentajeGlobal: totalTareas > 0 ? sumaPorcentajes / totalTareas : 0,
    },
    todasLasTareas,
  };
}

//
// 🔹 Cálculo ponderado
//
export function calculateWeightedProgress(rawData: any): ProjectProgress[] {
  const projects: ProjectProgress[] = [];

  for (const proyecto of rawData.data) {
    const actividades: ActivityProgress[] = [];
    let totalPesoProyecto = 0;
    let sumaPonderadaProyecto = 0;

    for (const actividad of proyecto.actividades) {
      const tareas: ProcessedTask[] = [];
      let totalPesoActividad = 0;
      let sumaPonderadaActividad = 0;

      for (const tarea of actividad.tareas) {
        const indicadoresProcesados: ProcessedIndicator[] = tarea.indicadores.map((ind: any) => {
          const planificado = Number.parseFloat(ind.cronograma.P);
          const ejecutado = Number.parseFloat(ind.cronograma.E);
          const porcentaje = ind.cronograma.porcentaje;

          return {
            id: ind.id,
            indicador: ind.indicador,
            meta: Number.parseFloat(ind.meta),
            fecha_inicio: new Date(ind.fecha_inicio),
            fecha_fin: new Date(ind.fecha_fin),
            planificado,
            ejecutado,
            porcentaje,
            estado: determineStatus(porcentaje),
          };
        });

        const porcentajePromedio =
          indicadoresProcesados.length > 0
            ? indicadoresProcesados.reduce((sum, ind) => sum + ind.porcentaje, 0) /
              indicadoresProcesados.length
            : 0;

        const tareaProcesada: ProcessedTask = {
          id: tarea.id,
          codigo: tarea.codigo_tarea,
          descripcion: tarea.descripcion,
          peso: tarea.pesoPorcentual,
          proyecto: proyecto.proyecto,
          proyectoCodigo: proyecto.codigo,
          fase: actividad.fase.nombre_corto,
          faseSigla: actividad.fase.sigla,
          actividad: actividad.descripcion,
          codigoActividad: actividad.codigo_actividad, // 🆕 agregado aquí también
          indicadores: indicadoresProcesados,
          porcentajePromedio,
          estado: determineStatus(porcentajePromedio),
        };

        tareas.push(tareaProcesada)
        totalPesoActividad += tarea.pesoPorcentual
        sumaPonderadaActividad += porcentajePromedio * tarea.pesoPorcentual
      }

      const porcentajePonderadoActividad =
        totalPesoActividad > 0 ? sumaPonderadaActividad / totalPesoActividad : 0

      actividades.push({
        id: actividad.id,
        codigo: actividad.codigo_actividad,
        descripcion: actividad.descripcion,
        fase: actividad.fase.nombre_corto,
        porcentajePonderado: porcentajePonderadoActividad,
        tareas,
      })

      totalPesoProyecto += totalPesoActividad
      sumaPonderadaProyecto += sumaPonderadaActividad
    }

    const porcentajePonderadoProyecto =
      totalPesoProyecto > 0 ? sumaPonderadaProyecto / totalPesoProyecto : 0

    projects.push({
      oid: proyecto.oid,
      codigo: proyecto.codigo,
      nombre: proyecto.proyecto,
      porcentajePonderado: porcentajePonderadoProyecto,
      actividades,
    })
  }

  return projects
}
